import { request } from "../../utils/request";
import { triggerBlobDownload } from "../../utils/batchVideoDownload";
import { getApiOrigin } from "../../config/apiBase";

/**
 * 用户发票管理接口
 * 接口文档：doc/发票接口.json
 * 路径前缀：/video/invoice
 */

// =====================================================================
// VO（与 OpenAPI schema 一一对应）
// =====================================================================

/** 发票汇总（累计充值 / 当前可开票） */
export interface InvoiceSummaryRespVO {
  totalRecharge: number;
  invoiceableAmount: number;
}

export interface CommonResultInvoiceSummaryRespVO {
  code: number;
  data: InvoiceSummaryRespVO;
  msg?: string;
}

/** 充值订单开票状态 */
export type InvoiceStatusCode =
  | "INVOICEABLE"
  | "INVOICING"
  | "NEED_FIX"
  | "ISSUED"
  | "VOIDED";

export interface InvoiceableOrderRespVO {
  orderId: number;
  orderNo: string;
  point: number;
  bonusPoint?: number;
  orderContent: string;
  amountYuan: number;
  payTime: string;
  payChannel?: string;
  paymentStatus?: string;
  paymentStatusLabel?: string;
  invoiceStatus: InvoiceStatusCode;
  invoiceStatusLabel: string;
  rejectionReason?: string;
  /** 退回原因中文文案（后端直接给，前端展示优先用这个） */
  rejectionReasonLabel?: string;
  rejectionNote?: string;
  applicationId?: number;
  canApply?: boolean;
  canDownload?: boolean;
  /** 订单类型（如 MEMBER_ORDER / POINTS_RECHARGE 等），用于回传开票申请 */
  orderType?: string;
}

export interface CommonResultListInvoiceableOrderRespVO {
  code: number;
  data: InvoiceableOrderRespVO[];
  msg?: string;
}

/** 分页结果（新增：用于支持服务端分页） */
export interface PageResultInvoiceableOrderRespVO {
  list: InvoiceableOrderRespVO[];
  total: number;
}

export interface CommonResultPageResultInvoiceableOrderRespVO {
  code: number;
  data: PageResultInvoiceableOrderRespVO;
  msg?: string;
}

/**
 * 服务端兼容两种返回形态：
 *  - 旧：`data: InvoiceableOrderRespVO[]`
 *  - 新：`data: { list: InvoiceableOrderRespVO[]; total: number }`
 * 调用方拿到的统一形状是 `{ list, total }`，后端升级后无需改调用方代码。
 */
export type InvoiceableOrderPagedResp = {
  list: InvoiceableOrderRespVO[];
  total: number;
};

/** 我的发票申请详情 */
export type UserInvoiceStatusCode =
  | "APPLYING"
  | "ISSUED"
  | "NEED_FIX"
  | "VOIDED";

export type InvoiceTypeCode = "ELECTRONIC_NORMAL" | "ELECTRONIC_SPECIAL";
export type TitleTypeCode = "COMPANY" | "PERSONAL";

export interface InvoiceApplicationRespVO {
  id: number;
  invoiceNo: string;
  userId?: number;
  userName?: string;
  orderId: number;
  orderNo?: string;
  amountYuan: number;
  invoiceType: InvoiceTypeCode;
  invoiceTypeLabel: string;
  titleType: TitleTypeCode;
  titleTypeLabel: string;
  titleName: string;
  taxNo?: string;
  email: string;
  userStatus: UserInvoiceStatusCode;
  userStatusLabel: string;
  taskStatus?: string;
  taskStatusLabel?: string;
  mailStatus?: string;
  mailStatusLabel?: string;
  assigneeId?: number;
  assigneeName?: string;
  pdfFileName?: string;
  pdfSize?: number;
  downloadable?: boolean;
  rejectionReason?: string;
  /** 退回原因中文文案（后端直接给，前端展示优先用这个） */
  rejectionReasonLabel?: string;
  rejectionNote?: string;
  createTime?: string;
  updateTime?: string;
  submittedTime?: string;
  completeTime?: string;
}

export interface CommonResultInvoiceApplicationRespVO {
  code: number;
  data: InvoiceApplicationRespVO;
  msg?: string;
}

export interface PageResultInvoiceApplicationRespVO {
  list: InvoiceApplicationRespVO[];
  total: number;
}

export interface CommonResultPageResultInvoiceApplicationRespVO {
  code: number;
  data: PageResultInvoiceApplicationRespVO;
  msg?: string;
}

/** 提交 / 重新提交开票申请 */
export interface InvoiceCreateReqVO {
  /** 首次提交必填；重新提交时按 URL 路径中的 ID 处理 */
  orderId?: number;
  /**
   * 订单类型，来自 /video/order/invoiceable 返回的 orderType 字段
   * （如 `MEMBER_ORDER`）
   */
  orderType?: string;
  invoiceType: InvoiceTypeCode | string;
  titleType: TitleTypeCode | string;
  titleName: string;
  taxNo?: string;
  email: string;
}

export interface CommonResultBoolean {
  code: number;
  data: boolean;
  msg?: string;
}

// =====================================================================
// 枚举映射
// =====================================================================

/** 内部 InvoiceStatusKey ↔ 后端 invoiceStatus 编码 */
export const INVOICE_STATUS_CODE_MAP: Record<string, InvoiceStatusCode> = {
  可开票: "INVOICEABLE",
  开票中: "INVOICING",
  待修改: "NEED_FIX",
  已开票: "ISSUED",
  已作废: "VOIDED",
};

export const INVOICE_STATUS_LABEL_MAP: Record<
  InvoiceStatusCode,
  "可开票" | "开票中" | "待修改" | "已开票" | "已作废"
> = {
  INVOICEABLE: "可开票",
  INVOICING: "开票中",
  NEED_FIX: "待修改",
  ISSUED: "已开票",
  VOIDED: "已作废",
};

/** 发票类型编码 ↔ 中文 */
export const INVOICE_TYPE_CODE_MAP = {
  电子普通发票: "ELECTRONIC_NORMAL",
  电子专用发票: "ELECTRONIC_SPECIAL",
} as const;

export const INVOICE_TYPE_LABEL_MAP: Record<InvoiceTypeCode, string> = {
  ELECTRONIC_NORMAL: "电子普通发票",
  ELECTRONIC_SPECIAL: "电子专用发票",
};

/** 抬头类型编码 ↔ 中文 */
export const TITLE_TYPE_CODE_MAP = {
  企业: "COMPANY",
  个人: "PERSONAL",
} as const;

export const TITLE_TYPE_LABEL_MAP: Record<TitleTypeCode, string> = {
  COMPANY: "企业",
  PERSONAL: "个人",
};

/** userStatus（开票记录） � 中文 */
export const USER_STATUS_LABEL_MAP: Record<
  UserInvoiceStatusCode,
  "开票中" | "已开票" | "待修改" | "已作废"
> = {
  APPLYING: "开票中",
  ISSUED: "已开票",
  NEED_FIX: "待修改",
  VOIDED: "已作废",
};

// =====================================================================
// API
// =====================================================================

/**
 * GET /video/invoice/summary
 * 查询发票汇总
 */
export const getInvoiceSummaryApi = (): Promise<CommonResultInvoiceSummaryRespVO> =>
  request<CommonResultInvoiceSummaryRespVO>("/video/invoice/summary", {
    method: "GET",
  });

/**
 * GET /video/order/invoiceable
 * 查询充值订单开票状态
 */
export interface GetInvoiceableOrdersParams {
  pageNo?: number;
  pageSize?: number;
  keyword?: string;
  invoiceStatus?: InvoiceStatusCode | "";
}
export const getInvoiceableOrdersApi = (
  params: GetInvoiceableOrdersParams
): Promise<
  | CommonResultListInvoiceableOrderRespVO
  | CommonResultPageResultInvoiceableOrderRespVO
> =>
  request<
    | CommonResultListInvoiceableOrderRespVO
    | CommonResultPageResultInvoiceableOrderRespVO
  >("/video/order/invoiceable", {
    method: "GET",
    params,
  });

/**
 * POST /video/invoice/application
 * 提交开票申请（首次）
 */
export const createInvoiceApplicationApi = (
  body: InvoiceCreateReqVO
): Promise<CommonResultInvoiceApplicationRespVO> =>
  request<CommonResultInvoiceApplicationRespVO>("/video/invoice/application", {
    method: "POST",
    body,
  });

/**
 * POST /video/invoice/application/{id}/resubmit
 * 修改并重新提交开票申请
 */
export const resubmitInvoiceApplicationApi = (
  id: number,
  body: InvoiceCreateReqVO
): Promise<CommonResultInvoiceApplicationRespVO> =>
  request<CommonResultInvoiceApplicationRespVO>(
    `/video/invoice/application/${id}/resubmit`,
    {
      method: "POST",
      body,
    }
  );

/**
 * GET /video/invoice/application/page
 * 分页查询我的开票记录
 */
export interface GetMyInvoiceApplicationsParams {
  pageNo: number;
  pageSize: number;
  userStatus?: UserInvoiceStatusCode | "";
}
export const getMyInvoiceApplicationsApi = (
  params: Partial<GetMyInvoiceApplicationsParams>
): Promise<CommonResultPageResultInvoiceApplicationRespVO> =>
  request<CommonResultPageResultInvoiceApplicationRespVO>(
    "/video/invoice/application/page",
    {
      method: "GET",
      params,
    }
  );

/**
 * GET /video/invoice/application/{id}
 * 查询我的发票申请详情（用于表单回填）
 */
export const getInvoiceApplicationDetailApi = (
  id: number
): Promise<CommonResultInvoiceApplicationRespVO> =>
  request<CommonResultInvoiceApplicationRespVO>(
    `/video/invoice/application/${id}`,
    { method: "GET" }
  );

/**
 * GET /video/invoice/application/{id}/download
 * 下载已开具发票 —— 直接走 fetch + blob，绕开 request.ts 的 JSON 解析
 */
export async function downloadInvoiceApplicationApi(
  id: number,
  fallbackFileName?: string
): Promise<{ ok: boolean; fileName?: string }> {
  const token = (localStorage.getItem("token") || "").trim();
  const baseURL = getApiOrigin();
  const url = `${baseURL}/video/invoice/application/${id}/download`;

  try {
    const res = await fetch(url, {
      method: "GET",
      headers: token
        ? {
            Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
          }
        : {},
    });
    if (!res.ok) return { ok: false };

    // 尝试从响应头拿文件名
    const cd = res.headers.get("content-disposition") || "";
    const fileNameMatch = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    const serverFileName = fileNameMatch?.[1]
      ? decodeURIComponent(fileNameMatch[1])
      : undefined;

    const blob = await res.blob();
    if (!blob.size) return { ok: false };
    const name =
      serverFileName || fallbackFileName || `invoice-${id}.pdf`;
    triggerBlobDownload(blob, name);
    return { ok: true, fileName: name };
  } catch {
    return { ok: false };
  }
}
